import { getCurrentUser } from "../store/authSlice";
import { useTypedSelector } from "./useTypedSelector";
import {
  pagesAccess,
  settingAccess,
  TRoleNameWithUnion,
} from "../../routing/accessMap";
import { IRole, TRoleName } from "../../model/IRole";
import { useMemo } from "react";

export type IAccessPagesMap =
  | Record<keyof typeof pagesAccess, boolean>
  | undefined;
export type IAccessPagesFilledMap = Exclude<IAccessPagesMap, undefined>;

export function useAccess() {
  const user = useTypedSelector(getCurrentUser());

  const ArrayWithoutRepeats = <T>(arr: Array<T>): Array<T> =>
    Array.from(new Set([...arr]));

  const getRoleTypes = (usr = user): TRoleName[] =>
    usr && usr?.roles?.length
      ? ArrayWithoutRepeats(usr.roles.map(({ name }) => name))
      : [];

  const getAccessMap = <T extends string | number | symbol>(
    accessMap: Record<T, string[]>
  ): Record<T, boolean> => {
    const userRoleNames: TRoleName[] = getRoleTypes();
    return Object.entries(accessMap).reduce(
      (acc, [pageUrl, allowedRoles]) => ({
        ...acc,
        [pageUrl]: (allowedRoles as TRoleNameWithUnion[]).some(
          (role) => role === "*" || userRoleNames.includes(role)
        ),
      }),
      {} as Record<T, boolean>
    );
  };

  const getAccess =
    <T extends string | number | symbol>(accessMap: Record<T, string[]>) =>
    (optionName: T): boolean => {
      console.log("Check access for", optionName);
      // -- Check if this resource is available for everyone
      if (accessMap?.[optionName] && accessMap[optionName].includes("*"))
        return true;

      const allowedRoleTypes = ArrayWithoutRepeats(
        accessMap?.[optionName] || []
      );
      const currentRoleTypes = getRoleTypes();

      return (
        new Set([...allowedRoleTypes, ...currentRoleTypes]).size <
        allowedRoleTypes.length + currentRoleTypes.length
      );
    };

  const pageAccessMap = useMemo(
    () => getAccessMap(pagesAccess),
    [user?.idUser]
  );

  const settingAccessMap = useMemo(
    () => getAccessMap(settingAccess),
    [user?.idUser]
  );

  return {
    getPageAccess: getAccess(pagesAccess),
    getSettingAccess: getAccess(settingAccess),
    pageAccessMap,
    settingAccessMap,
  };
}
